/**
 * Pure FNA calculation helpers — shared by editor and report so they never disagree.
 * Amounts are treated as numbers; null/undefined coerce to 0.
 */

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Convert a periodic amount to an annual figure.
 * weekly → ×52, annual/yearly → ×1, monthly (default/else) → ×12
 */
export function annualizeAmount(amount, frequency) {
  const n = toNumber(amount);
  const freq = String(frequency || 'monthly').toLowerCase();
  if (freq === 'weekly') return n * 52;
  if (freq === 'annual' || freq === 'yearly') return n * 1;
  return n * 12;
}

export function calculateTotalAnnualIncome(incomeSources = []) {
  return (incomeSources || []).reduce(
    (sum, row) => sum + annualizeAmount(row.amount, row.frequency),
    0
  );
}

export function calculateTotalAnnualExpenses(expenses = []) {
  return (expenses || []).reduce(
    (sum, row) => sum + annualizeAmount(row.amount, row.frequency),
    0
  );
}

export function calculateNetIncome(incomeSources, expenses) {
  return calculateTotalAnnualIncome(incomeSources) - calculateTotalAnnualExpenses(expenses);
}

export function calculateNetWorth(assets = [], liabilities = []) {
  const totalAssets = (assets || []).reduce((sum, row) => sum + toNumber(row.amount), 0);
  const totalLiabilities = (liabilities || []).reduce((sum, row) => sum + toNumber(row.amount), 0);
  return totalAssets - totalLiabilities;
}

/** Human Life Value / Family Income Need: 20× annual income. */
export function calculateFIN(incomeSources) {
  return calculateTotalAnnualIncome(incomeSources) * 20;
}

/** FIRE number: 25× annual expenses (financial independence). Distinct from calculateFIN. */
export function calculateFinancialIndependenceNumber(expenses) {
  return calculateTotalAnnualExpenses(expenses) * 25;
}

const LIQUID_ASSET_CATEGORIES = new Set(['checking', 'savings', 'investments']);
const RETIREMENT_ASSET_CATEGORIES = new Set(['retirement', '401k_403b', 'ira_sep', 'roth_ira']);

/**
 * Insurance need analysis. Auto-derives inputs from FNA child rows.
 *
 * coverageGap equals additionalCoverageNeeded — do NOT subtract existingCoverage
 * again. The old app double-subtracted policy coverage: it was already netted into
 * totalResources when computing additionalCoverageNeeded = totalNeeds - totalResources.
 */
export function calculateInsuranceNeed({
  incomeSources = [],
  assets = [],
  insurancePolicies = [],
  yearsToReplace = 20,
  finalExpenses = 0,
  educationFund = 0,
} = {}) {
  const annualIncome = calculateTotalAnnualIncome(incomeSources);
  const existingCoverage = (insurancePolicies || []).reduce(
    (sum, row) => sum + toNumber(row.coverage_amount),
    0
  );
  const liquidAssets = (assets || []).reduce((sum, row) => {
    if (LIQUID_ASSET_CATEGORIES.has(row.category)) return sum + toNumber(row.amount);
    return sum;
  }, 0);
  const retirementAccounts = (assets || []).reduce((sum, row) => {
    if (RETIREMENT_ASSET_CATEGORIES.has(row.category)) return sum + toNumber(row.amount);
    return sum;
  }, 0);

  const years = toNumber(yearsToReplace) || 20;
  const finalExpensesN = toNumber(finalExpenses);
  const educationFundN = toNumber(educationFund);

  const totalNeeds = annualIncome * years + finalExpensesN + educationFundN;
  const totalResources = existingCoverage + liquidAssets + retirementAccounts;
  const additionalCoverageNeeded = Math.max(0, totalNeeds - totalResources);

  // Correct gap: additionalCoverageNeeded already accounts for existingCoverage
  // via totalResources. Do not subtract existingCoverage a second time (old-app bug).
  const coverageGap = additionalCoverageNeeded;

  return {
    annualIncome,
    existingCoverage,
    liquidAssets,
    retirementAccounts,
    yearsToReplace: years,
    finalExpenses: finalExpensesN,
    educationFund: educationFundN,
    totalNeeds,
    totalResources,
    additionalCoverageNeeded,
    coverageGap,
  };
}

const MS_PER_30_DAYS = 30 * 24 * 60 * 60 * 1000;

export function calculateGoalProgress(goal = {}) {
  const currentAmount = toNumber(goal.current_amount ?? goal.currentAmount);
  const targetAmount = toNumber(goal.target_amount ?? goal.targetAmount);
  const rawDate = goal.target_date ?? goal.targetDate;

  const progressPct =
    targetAmount > 0 ? Math.min((currentAmount / targetAmount) * 100, 100) : 0;

  let monthsRemaining = 0;
  if (rawDate) {
    const target = new Date(rawDate);
    if (!Number.isNaN(target.getTime())) {
      const today = new Date();
      monthsRemaining = Math.max(0, Math.ceil((target.getTime() - today.getTime()) / MS_PER_30_DAYS));
    }
  }

  const remaining = Math.max(0, targetAmount - currentAmount);
  const monthlyRequired = monthsRemaining === 0 ? 0 : remaining / monthsRemaining;

  return {
    progressPct,
    monthsRemaining,
    monthlyRequired: Math.max(0, monthlyRequired),
  };
}

/** Count of estate checklist items flagged as needing attention. */
export function calculateEstateNeedsAttentionCount(estateChecklist = []) {
  return (estateChecklist || []).filter((item) => Boolean(item.needs_attention)).length;
}

/** Percent of the 8 estate checklist items flagged as needing attention. */
export function calculateEstateNeedsAttentionPct(estateChecklist = []) {
  return (calculateEstateNeedsAttentionCount(estateChecklist) / 8) * 100;
}

function findChecklistItem(estateChecklist, itemKey) {
  return (estateChecklist || []).find((item) => item.item_key === itemKey);
}

/**
 * Ordered recommendation rules. If none fire, returns a single positive message.
 */
export function getKeyRecommendations({
  netIncome = 0,
  annualIncome = 0,
  insuranceGap = 0,
  estateChecklist = [],
  totalAssets = 0,
  totalLiabilities = 0,
} = {}) {
  const recommendations = [];
  const net = toNumber(netIncome);
  const annual = toNumber(annualIncome);
  const gap = toNumber(insuranceGap);
  const assets = toNumber(totalAssets);
  const liabilities = toNumber(totalLiabilities);

  if (net < 0) {
    const monthlyShortfall = Math.abs(net) / 12;
    recommendations.push(
      `Negative cash flow: annual expenses exceed income. Monthly shortfall is approximately $${monthlyShortfall.toLocaleString(undefined, { maximumFractionDigits: 0 })}.`
    );
  }

  if (net > 0 && annual > 0 && net < 0.2 * annual) {
    const savingsRatePct = (net / annual) * 100;
    recommendations.push(
      `Improve your savings rate — currently about ${savingsRatePct.toFixed(1)}% of annual income (target 20%+).`
    );
  }

  if (gap > 0) {
    recommendations.push(
      `Life insurance gap of approximately $${gap.toLocaleString(undefined, { maximumFractionDigits: 0 })} — consider additional coverage.`
    );
  }

  const will = findChecklistItem(estateChecklist, 'will');
  if (will?.needs_attention === true) {
    recommendations.push('Create a will to protect your family and document your wishes.');
  }

  if (assets > 0 && liabilities > 0.5 * assets) {
    const debtToAssetPct = (liabilities / assets) * 100;
    recommendations.push(
      `Focus on debt reduction — liabilities are ${debtToAssetPct.toFixed(0)}% of total assets.`
    );
  }

  const emergencyFund = findChecklistItem(estateChecklist, 'emergencyFund');
  if (emergencyFund?.needs_attention === true) {
    recommendations.push('Build a 3–6 month emergency fund for unexpected expenses.');
  }

  if (recommendations.length === 0) {
    return ['You have a strong financial foundation — keep reviewing goals and coverage annually.'];
  }

  return recommendations;
}
